import type { GeneratedCeiling } from "building-types/ceilings/ceiling";
import { CeilingType } from "building-types/ceilings/ceiling";
import type { TrimEdgeBehavior } from "building-types/edgeBehavior/edgeBehavior";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { CrownTrimMolding } from "building-types/molding/trimMoldingTypes";
import { crownTrimProfilePolyLineByName } from "building-types/profiles/defaultBoardTrimProfiles";
import type { BoardDescriptor, CustomDescriptor } from "building-types/profiles/profileTypes";
import { ProfileExtrusionDescriptorType } from "building-types/profiles/profileTypesEnum";
import type { RoomSet } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { InteriorTrimSettings } from "building-types/settings/interiorTrimSettings";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallMassSystem, WallSystem } from "building-types/walls/wallSytemTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { aabb2Width } from "geom/aabb2Types";
import type { BRep } from "geom/brep";
import { classifyContainmentPolyLine2PolyLine2, PolyLine2PolyLine2ContainmentClass } from "geom/containment";
import { COMPACTNESS_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED, ONE_MINUS_DIST_EPSILON } from "geom/core";
import { extrudeFaceAlongPolyLine3 } from "geom/extrude";
import { joinPolyLine3Rails } from "geom/join";
import { line2Collinear } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import { polyLine2OnPlane } from "geom/plane";
import { polygonUnion } from "geom/polygon";
import {
  polyLine2AABB2,
  polyLine2ByTwoPoints,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2SetCCW,
  polyLine2SetCW,
  polyLine2TrimEnds,
} from "geom/polyLine2";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3ByTwoPoints,
  polyLine3Concat,
  polyLine3EdgeTangent,
  polyLine3Length,
  polyLine3ToPolyLine2IgnoreZ,
} from "geom/polyLine3";
import { vec2IsBetweenUsingLengthBasedCalc } from "geom/vec2";
import { vec3DistSquared, vec3Dot } from "geom/vec3";
import { pushMissingProfileReferenceIssue } from "../elements/profileMissingReference";
import { getActiveProfileDescriptorOrUndefined } from "../elements/profileTools";
import { crownTrimMoldingNew } from "../elements/trimMolding";
import { getWallPath } from "../elements/walls/wall";
import { solidWallLayerWithFallback } from "../elements/walls/wallLayers";
import { isRoomSetProcessed } from "./generateBaseTrimMoldingTools";
import { getFloorElevationAndCeilingHeight } from "./generateCeilingsTools";

interface MakeCrownTrimMoldingOptions {
  level: Level;
  walls: Table<WallSystem>;
  rooms: Table<Room>;
  roomFloorAssemblies: Table<FloorAssembly>;
  wallMasses: Table<WallMassSystem>;
  trimEdgeBehaviors: Table<TrimEdgeBehavior>;
  profileSource: ActiveProfileSource;
  interiorTrim?: InteriorTrimSettings;
  ceilings: Table<GeneratedCeiling>;
  ceilingsByLevel: Table<string[]>;
  crownTrimMoldings: RWTable<CrownTrimMolding>;
  issues?: BuildingGenerationIssue[];
}

export function makeCrownTrimMolding({
  level,
  walls,
  rooms,
  roomFloorAssemblies,
  wallMasses,
  trimEdgeBehaviors,
  profileSource,
  interiorTrim,
  ceilings,
  ceilingsByLevel,
  crownTrimMoldings,
  issues,
}: MakeCrownTrimMoldingOptions) {
  assertIsNotUndefined(interiorTrim);
  const { crownMoldingType, crownMoldingMaterial } = interiorTrim;

  let crownTrimProfile: PolyLine2 | undefined;

  if (crownMoldingType === undefined) {
    return undefined;
  }

  const crownMoldingDescriptor = getActiveProfileDescriptorOrUndefined(profileSource, crownMoldingType);
  if (crownMoldingDescriptor?.profileType === ProfileExtrusionDescriptorType.BOARD) {
    const { width, height } = crownMoldingDescriptor as BoardDescriptor;
    crownTrimProfile = polyLine2New([0, 0, 0, -width, -height, -width, -height, 0, 0, 0]);
  } else if (crownMoldingDescriptor?.profileType === ProfileExtrusionDescriptorType.CUSTOM) {
    const { polyline } = crownMoldingDescriptor as CustomDescriptor;
    crownTrimProfile = polyLine2New(polyline);
  } else if (profileSource.settingsToCatalogs) {
    pushMissingProfileReferenceIssue(
      issues,
      profileSource,
      crownMoldingType,
      interiorTrim.id ? { type: "SETTINGS" as const, id: interiorTrim.id } : undefined,
      "crownMoldingType",
    );
    return undefined;
  } else {
    crownTrimProfile = crownTrimProfilePolyLineByName[crownMoldingType];
  }

  if (!crownTrimProfile) {
    return undefined;
  }

  const profileAabb = polyLine2AABB2(crownTrimProfile);
  const minRailThickness = aabb2Width(profileAabb) + DIST_EPSILON;

  function validRailForConcat(prevRail: PolyLine3, nextRail: PolyLine3): boolean {
    const prevL = prevRail.length;
    if (
      vec3DistSquared(
        prevRail[prevL - 3],
        prevRail[prevL - 2],
        prevRail[prevL - 1],
        nextRail[0],
        nextRail[1],
        nextRail[2],
      ) > DIST_EPSILON_SQUARED ||
      Math.abs(prevRail[prevL - 1] - nextRail[2]) > DIST_EPSILON
    ) {
      return false;
    }
    const [t1X, t1Y, t1Z] = polyLine3EdgeTangent(prevRail, prevL / 2 - 2);
    const [t2X, t2Y, t2Z] = polyLine3EdgeTangent(nextRail, 0);

    // discard 180º turn
    return vec3Dot(t1X, t1Y, t1Z, t2X, t2Y, t2Z) > -ONE_MINUS_DIST_EPSILON;
  }

  function segmentOverlappingOnAnyPolyEdge(x1: number, y1: number, x2: number, y2: number, pl: PolyLine2) {
    const line = polyLine2ByTwoPoints(x1, y1, x2, y2);
    polyLine2TrimEnds(line, COMPACTNESS_EPSILON);
    const edge0 = polyLine2Edge(line, 0);
    for (let i = 0, l = polyLine2EdgeCount(pl); i < l; i++) {
      const edge = polyLine2Edge(pl, i);
      if (
        line2Collinear(
          edge0[0],
          edge0[1],
          edge0[2],
          edge0[3],
          edge[0],
          edge[1],
          edge[2],
          edge[3],
          COMPACTNESS_EPSILON,
        ) &&
        (vec2IsBetweenUsingLengthBasedCalc(
          edge[0],
          edge[1],
          edge[2],
          edge[3],
          edge0[0],
          edge0[1],
          COMPACTNESS_EPSILON,
        ) ||
          vec2IsBetweenUsingLengthBasedCalc(
            edge[0],
            edge[1],
            edge[2],
            edge[3],
            edge0[2],
            edge0[3],
            COMPACTNESS_EPSILON,
          ))
      ) {
        return true;
      }
    }
    return false;
  }

  type TrimRail = { currentRail: PolyLine3 | null; trimRails: PolyLine3[] };

  const addTrimRail = (
    x0: number,
    y0: number,
    z0: number,
    x1: number,
    y1: number,
    z1: number,
    currentTrimRail: TrimRail,
  ) => {
    const newRail = polyLine3ByTwoPoints(x0, y0, z0, x1, y1, z1);

    if (polyLine3Length(newRail) < minRailThickness) {
      return;
    }

    if (currentTrimRail.currentRail) {
      if (validRailForConcat(currentTrimRail.currentRail, newRail)) {
        currentTrimRail.currentRail = polyLine3Concat(currentTrimRail.currentRail, newRail, false);
      } else {
        currentTrimRail.trimRails.push(currentTrimRail.currentRail);
        currentTrimRail.currentRail = null;
      }
    }

    if (!currentTrimRail.currentRail) {
      currentTrimRail.currentRail = newRail;
    }
  };

  const { id: levelId } = level;
  const roomSets = wallMasses[levelId].openConnectedRoomSets;
  const ceilingIds = ceilingsByLevel[levelId];
  const roomSetProcessed: RoomSet[] = [];
  const wallIndex = walls[levelId].wallIndex;
  const trimEdgeBehaviorByRoomEdgeKey = new Map<string, TrimEdgeBehavior>();
  for (const trimEdgeBehavior of Object.values(trimEdgeBehaviors)) {
    trimEdgeBehaviorByRoomEdgeKey.set(`${trimEdgeBehavior.parentId}:${trimEdgeBehavior.edgeId}`, trimEdgeBehavior);
  }

  const enabledCrownTrimWallIdsByRoom = new Map<string, Set<string>>();
  for (const room of Object.values(rooms)) {
    const enabledWallIds = new Set<string>();
    const roomWallsForCrown = wallIndex.wallsInRoom(room);
    for (const wall of roomWallsForCrown) {
      const roomEdgeIndex = wallIndex.wallParentRoomEdgeIndex(room, wall);
      if (roomEdgeIndex < 0) {
        continue;
      }
      const roomEdgeId = room.edgeIds[roomEdgeIndex];
      const trimEdgeBehavior = trimEdgeBehaviorByRoomEdgeKey.get(`${room.id}:${roomEdgeId}`);
      const hasCrownTrimMolding = trimEdgeBehavior?.hasCrownTrimMolding ?? room.attributes.hasCrownTrimMolding;
      if (hasCrownTrimMolding) {
        enabledWallIds.add(wall.id);
      }
    }
    enabledCrownTrimWallIdsByRoom.set(room.id, enabledWallIds);
  }

  type TrimElevationGroupTable = { [trimElevation: number]: PolyLine2Polygon[] };

  for (const roomSet of roomSets) {
    if (roomSet.length === 0) {
      continue;
    }

    //roomSets have duplicated information for room sets that are connected to each other, so we only process the first one
    if (isRoomSetProcessed(roomSet, roomSetProcessed)) {
      continue;
    }

    const roomSetCeilingOutlines: TrimElevationGroupTable = {};
    const roomSetSubCeilingTrayOutlines: TrimElevationGroupTable = {};
    const roomSetWallCrossSections: PolyLine2[] = [];
    for (const room of roomSet) {
      const enabledCrownTrimWallIds = enabledCrownTrimWallIdsByRoom.get(room.id);
      if (!room || trimNotNeeded(room, level, enabledCrownTrimWallIds)) {
        continue;
      }

      //collect ceiling and solid wall faces for the room
      for (const ceilingId of ceilingIds) {
        const ceiling = ceilings[ceilingId];

        if (!ceiling) {
          continue;
        }

        const { section, subCeilings } = ceiling;
        const { roomId, ceilingFaces } = section;

        //get the floor elevation and ceiling height
        const { floorElevation, ceilingHeight } = getFloorElevationAndCeilingHeight(room, roomFloorAssemblies[roomId]);
        let trimElevation: number = floorElevation + ceilingHeight;

        //continue if ceiling faces are not associated with a room
        if (rooms[roomId].id !== room.id) {
          continue;
        }

        const roomWalls = wallIndex.wallsInRoom(room);
        const roomWallCrossSections = roomWalls.reduce((acc, wall) => {
          if (!enabledCrownTrimWallIds?.has(wall.id)) {
            return acc;
          }

          if (wall.wallType === WallType.SOLID) {
            const solidWallMass = solidWallLayerWithFallback(wall, WallLayerType.SUBSTRATE);
            const { wallDescriptor, leftRoom, rightRoom } = getWallPath(wall);
            const { leftSide, rightSide } = wallDescriptor;
            let wallFinishTop: number = 0;

            if (leftRoom && leftRoom.id === room.id) {
              wallFinishTop = leftSide.wallSideDatums.ceilingBottom;
            } else if (rightRoom && rightRoom.id === room.id) {
              wallFinishTop = rightSide.wallSideDatums.ceilingBottom;
            } else {
              return acc;
            }

            const wallTopElevation = solidWallMass.floorElevation + wallFinishTop;

            if (Math.abs(wallTopElevation - trimElevation) < DIST_EPSILON) {
              const { footprint: solidWallCrossSection } = solidWallMass;

              if (solidWallCrossSection.length !== 0) {
                acc.push(solidWallCrossSection);
              }
            }
          }
          return acc;
        }, [] as PolyLine2[]);
        roomSetWallCrossSections.push(...roomWallCrossSections);

        if (ceilingFaces.length !== 0) {
          for (const faces of ceilingFaces) {
            const { loops, plane } = faces;
            trimElevation = plane.originZ;

            if (loops.length) {
              if (!roomSetCeilingOutlines[trimElevation]) {
                roomSetCeilingOutlines[trimElevation] = [];
              }
              const transformedLoop3D = polyLine2OnPlane(plane, loops[0]);
              const transformedLoop2D = polyLine3ToPolyLine2IgnoreZ(transformedLoop3D);
              roomSetCeilingOutlines[trimElevation].push([transformedLoop2D]);
            }
          }
        }
        if (subCeilings.length !== 0) {
          for (const subCeiling of subCeilings) {
            if (
              subCeiling.type === CeilingType.TRAY_FREEFORM ||
              subCeiling.type === CeilingType.TRAY_SIMPLE_OFFSET ||
              subCeiling.type === CeilingType.FLAT
            ) {
              const { sections } = subCeiling;
              if (sections && sections.length !== 0) {
                const { ceilingFaces } = sections[0];
                for (const faces of ceilingFaces) {
                  const { loops, plane } = faces;
                  trimElevation = plane.originZ;

                  if (loops.length) {
                    // Flat types go to main ceiling, tray types go to sub-ceiling outlines.
                    let outlineCollection: TrimElevationGroupTable | null = null;
                    if (subCeiling.type === CeilingType.FLAT) {
                      outlineCollection = roomSetCeilingOutlines;
                    } else if (enabledCrownTrimWallIds && enabledCrownTrimWallIds.size > 0) {
                      outlineCollection = roomSetSubCeilingTrayOutlines;
                    }

                    if (!outlineCollection) {
                      continue;
                    }

                    if (!outlineCollection[trimElevation]) {
                      outlineCollection[trimElevation] = [];
                    }
                    const transformedLoop3D = polyLine2OnPlane(plane, loops[0]);
                    const transformedLoop2D = polyLine3ToPolyLine2IgnoreZ(transformedLoop3D);
                    outlineCollection[trimElevation].push([transformedLoop2D]);
                  }
                }
              }
            } else if (subCeiling.type === CeilingType.VAULTED) {
              //for vaulted ceilings the outline stores the ceiling outline and not the valuted outline
              //TODO: expand support for vaulted ceilings if trim needs to be added to the sloped edges.
              //This implementation only adds trim at the base outline of the vaulted ceiling
              const { outline } = subCeiling;
              if (outline && outline.length) {
                if (!roomSetCeilingOutlines[trimElevation]) {
                  roomSetCeilingOutlines[trimElevation] = [];
                }
                roomSetCeilingOutlines[trimElevation].push([outline]);
              }
            }
          }
        }
      }
    }

    //add trims for the subceiling tray outlines. this processing is easy
    const trims: BRep[] = [];
    for (const trimElevation in roomSetSubCeilingTrayOutlines) {
      const trayOutline = roomSetSubCeilingTrayOutlines[trimElevation];
      for (let i = 0; i < trayOutline.length; i++) {
        const outlines = trayOutline[i];

        for (let j = 0; j < outlines.length; j++) {
          const outline = outlines[j];
          polyLine2SetCCW(outline);
          const currentTrimRail: TrimRail = { currentRail: null, trimRails: [] };

          for (let k = 0; k < outline.length; k += 2) {
            const x0 = outline[k];
            const y0 = outline[k + 1];
            const x1 = outline[(k + 2) % outline.length];
            const y1 = outline[(k + 3) % outline.length];
            const z0 = Number(trimElevation);
            const z1 = Number(trimElevation);
            addTrimRail(x0, y0, z0, x1, y1, z1, currentTrimRail);
          }

          if (currentTrimRail.currentRail) {
            if (
              currentTrimRail.trimRails.length > 0 &&
              validRailForConcat(currentTrimRail.currentRail, currentTrimRail.trimRails[0])
            ) {
              currentTrimRail.trimRails[0] = polyLine3Concat(
                currentTrimRail.currentRail,
                currentTrimRail.trimRails[0],
                false,
              );
            } else {
              currentTrimRail.trimRails.push(currentTrimRail.currentRail);
            }
          }

          if (currentTrimRail.trimRails.length !== 0) {
            const railConnected = joinPolyLine3Rails({
              segments: currentTrimRail.trimRails,
              searchBoxTolerance: DIST_EPSILON,
              simplify: true,
            });
            for (let r = 0, lr = railConnected.length; r < lr; r++) {
              trims.push(extrudeFaceAlongPolyLine3([crownTrimProfile], railConnected[r], 0, 0, 1));
            }
          }
        }
      }
    }

    let railConnected: PolyLine3[] = [];
    //add trims for the ceiling outlines. this processing is more complex as outlines needs to be combined and also their intesections with walls have to be calculated to create the trim rails in the correct locations
    for (const trimElevation in roomSetCeilingOutlines) {
      if (!roomSetCeilingOutlines[trimElevation]) {
        continue;
      }
      const roomSetCeilingOutline = polygonUnion(roomSetCeilingOutlines[trimElevation]);

      for (let i = 0; i < roomSetCeilingOutline.length; i++) {
        const outlines = roomSetCeilingOutline[i];

        for (let j = 0; j < outlines.length; j++) {
          const outline = outlines[j];
          !j ? polyLine2SetCCW(outline) : polyLine2SetCW(outline);
          const currentTrimRail: TrimRail = { currentRail: null, trimRails: [] };

          for (let k = 0; k < outline.length; k += 2) {
            const x0 = outline[k];
            const y0 = outline[k + 1];
            const x1 = outline[(k + 2) % outline.length];
            const y1 = outline[(k + 3) % outline.length];
            const z0 = Number(trimElevation);
            const z1 = Number(trimElevation);
            let addTrim: boolean = false;
            const line = polyLine2ByTwoPoints(x0, y0, x1, y1);
            for (const crossSection of roomSetWallCrossSections) {
              polyLine2SetCCW(crossSection);
              const inchBiggerCrossSection = offsetPolyLine2(crossSection, 1);
              const cclassBigger = classifyContainmentPolyLine2PolyLine2(line, inchBiggerCrossSection);
              if (
                cclassBigger === PolyLine2PolyLine2ContainmentClass.INSIDE ||
                cclassBigger === PolyLine2PolyLine2ContainmentClass.COINCIDENT ||
                cclassBigger === PolyLine2PolyLine2ContainmentClass.INSIDE_COINCIDENT
              ) {
                addTrim = true;
                break;
              } else if (segmentOverlappingOnAnyPolyEdge(x0, y0, x1, y1, crossSection)) {
                addTrim = true;
                break;
              }
            }
            if (addTrim) {
              addTrimRail(x0, y0, z0, x1, y1, z1, currentTrimRail);
            }
          }

          if (currentTrimRail.currentRail) {
            if (
              currentTrimRail.trimRails.length > 0 &&
              validRailForConcat(currentTrimRail.currentRail, currentTrimRail.trimRails[0])
            ) {
              currentTrimRail.trimRails[0] = polyLine3Concat(
                currentTrimRail.currentRail,
                currentTrimRail.trimRails[0],
                false,
              );
            } else {
              currentTrimRail.trimRails.push(currentTrimRail.currentRail);
            }
          }

          if (currentTrimRail.trimRails.length !== 0) {
            railConnected = joinPolyLine3Rails({
              segments: currentTrimRail.trimRails,
              searchBoxTolerance: DIST_EPSILON,
              simplify: true,
            });
            for (let r = 0, lr = railConnected.length; r < lr; r++) {
              trims.push(extrudeFaceAlongPolyLine3([crownTrimProfile], railConnected[r], 0, 0, 1));
            }
          }
        }
      }
    }

    const finalTrim = crownTrimMoldingNew(levelId, roomSet, trims, crownMoldingMaterial, railConnected);
    crownTrimMoldings[finalTrim.id] = finalTrim;
  }

  return crownTrimMoldings;
}

//this function returns true if the trim rail is not needed
//it is currently forcing no trim at the stairs level if there is a level above
function trimNotNeeded(room: Room, level: Level, enabledCrownTrimWallIds: ReadonlySet<string> | undefined): boolean {
  if (
    (room.type === RoomType.STAIRS && level.levelAbove) ||
    (room.type === RoomType.STAIRS_EDITOR && level.levelAbove)
  ) {
    return true;
  }

  if (!enabledCrownTrimWallIds || enabledCrownTrimWallIds.size === 0) {
    return true;
  }

  return false;
}
